/**
 * HDMX PDP page
 */

import { ProductDetailsAddToCart } from '@/components/blocks/ProductDetails/AddToCart';
import { ProductDetailsDisplay } from '@/components/blocks/ProductDetails/Display';
import { ProductAvailability } from '@/components/blocks/ProductDetails/parts/ProductAvailability';
import { PromotionCard } from '@/components/blocks/PromotionCard';
import { EMarketingSpot } from '@/components/content/EMarketingSpot';
import {
	AR_VISUAL_SUPPORT_VALUE,
	HDM_NOT_AVAILABLE_BODY_CLASS,
	PDP_CROSS_SELL_ESPOT,
	PDP_NOT_AVAILABLE_SKU_ESPOT,
	PDP_PRICE_INFERIOR_SECTION_ESPOT,
	PDP_PRICE_SUPERIOR_SECTION_ESPOT,
	PDP_ROOMVO_TRIGGER_ESPOT,
	PDP_UX_COMPONENT_ESPOT,
	ROOMVO_VISUAL_SUPPORT_VALUE,
	VISUAL_SUPPORT_ATTRIBUTE_IDENTIFIER,
} from '@/components/content/ProductDetails/constants';
import { ProductDetailsAttributes } from '@/components/content/ProductDetails/parts/Attributes';
import { DeliveryMethodSelector } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector';
import { PaperCard } from '@/components/content/ProductDetails/parts/PaperCards';
import {
	priceAndPromoCardSX,
	promoCardSX,
} from '@/components/content/ProductDetails/styles/priceAndPromoCard';
import { productDetailsDisplaySX } from '@/components/content/ProductDetails/styles/productDetails';

import { FreeGiftPromotionCard } from '@/components/blocks/FreeGiftPromotionCard';
import { HdmNotAvailable } from '@/components/blocks/HdmNotAvailable';
import { useIsStoreOnlyAvailable } from '@/components/blocks/PLPProductCard/hooks/useIsStoreOnlyAvailable';
import { ProductDetailsPrice } from '@/components/blocks/ProductDetails/Price';
import { ProductGalleryTabs } from '@/components/blocks/ProductDetails/ProductGalleryTabs';
import { RoomvoContainer } from '@/components/blocks/ProductDetails/RoomvoContainer';
import { VideoGalleryProvider } from '@/components/blocks/ProductDetails/context/VideoGalleryContext';
import { ProductAbout } from '@/components/blocks/ProductDetails/parts/ProductAbout';
import {
	NAVIGABLE_SECTION_IDS,
	useNavigableSection,
} from '@/components/content/ProductDetails/hooks/useNavigableSection';
import { MetadataScriptPDP } from '@/components/content/ProductDetails/parts/MetadataScriptPDP/MetadataScriptPDP';
import {
	Sentinel,
	StickyProductHeader,
} from '@/components/content/ProductDetails/parts/StickyProductHeader';
import { galleryPaperSX, galleryStackSX } from '@/components/content/ProductDetails/styles/gallery';
import { useVNTANA360AndQRImageUrlDetails } from '@/components/content/ProductDetails/use-vntana-360-and-qr-image-url-details';
import { ProductDetailsSkeleton } from '@/components/skeleton/components/elements/PDP';
import { useDefaultStore } from '@/data/Content/DefaultStore';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSettings } from '@/data/Settings';
import { TYPES } from '@/data/constants/product';
import { ContentProvider } from '@/data/context/content';
import { ProductType } from '@/data/types/Product';
import { WidgetComponentProps } from '@/data/types/Slot';
import { Box, Paper, Stack } from '@mui/material';
import { useAdobePDPViews } from 'presentation/events/hooks/adobe/useAdobePDPViews';
import { usePDPTrackLoadEvents } from 'presentation/events/hooks/usePDPTrackLoadEvents';
import { getItemPartNumber } from 'presentation/events/utils/productUtils';
import { FC, useEffect, useRef } from 'react';

// eslint-disable-next-line complexity
export const ProductDetails: FC<WidgetComponentProps> = ({ id }) => {
	const navigableSectionProps = useNavigableSection({
		sectionId: NAVIGABLE_SECTION_IDS.PRODUCT_DETAILS,
	});

	const { uniqueId: physicalStoreId } = useDefaultStore();
	const { settings } = useSettings();
	const productDetails = useProductDetails({
		partNumber: id.toString(),
		physicalStoreIds: [physicalStoreId ?? ''],
	});
	const { product, selection, isBuyable, isLoading } = productDetails;

	const visualSupportAttributeValues =
		selection?.sku?.attributes
			?.find((att) => att.identifier === VISUAL_SUPPORT_ATTRIBUTE_IDENTIFIER)
			?.values?.flatMap((v) => (Array.isArray(v.value) ? v.value : [v.value])) ?? [];

	const isRoomvoVisualSupportEnabled = visualSupportAttributeValues.includes(
		ROOMVO_VISUAL_SUPPORT_VALUE
	);
	const isRoomvoEnabled = settings?.userData?.ENABLE_ROOMVO === 'true';

	const isVisualSupportEnabled = visualSupportAttributeValues.includes(AR_VISUAL_SUPPORT_VALUE);
	const { arQrImageBase64, isArEnabled, view360URL } = useVNTANA360AndQRImageUrlDetails({
		sku: id.toString(),
		storeConfData: settings.userData,
		isVisualSupportEnabled,
	});

	useEffect(() => {
		const isUnavailable = !isLoading && (!selection || !product?.partNumber);
		if (isUnavailable) {
			document.body.classList.add(HDM_NOT_AVAILABLE_BODY_CLASS);
		} else {
			document.body.classList.remove(HDM_NOT_AVAILABLE_BODY_CLASS);
		}
		return () => {
			document.body.classList.remove(HDM_NOT_AVAILABLE_BODY_CLASS);
		};
	}, [product, selection, isLoading]);

	const isStoreOnlyAvailable = useIsStoreOnlyAvailable({
		product: (selection?.sku ?? product) as ProductType,
	});

	usePDPTrackLoadEvents({
		product: selection?.sku ?? product,
		isStoreOnlyAvailable,
	});

	useAdobePDPViews(selection?.sku?.partNumber ?? product?.partNumber);

	const navigationBarSentinelRef = useRef<HTMLDivElement>(null);
	const productBarSentinelRef = navigableSectionProps.ref;

	if (isLoading) {
		return <ProductDetailsSkeleton />;
	}

	if (!selection || !product?.partNumber || !isBuyable) {
		return (
			<HdmNotAvailable
				emsName={PDP_NOT_AVAILABLE_SKU_ESPOT}
				sx={{
					width: '100%',
				}}
			/>
		);
	}

	return (
		<ContentProvider value={productDetails}>
			<MetadataScriptPDP product={selection?.sku} />

			<StickyProductHeader
				navigationBarSentinelRef={navigationBarSentinelRef}
				productBarSentinelRef={productBarSentinelRef}
			/>

			<Stack
				direction={{
					md: 'row',
				}}
				justifyContent="space-around"
				gap={1.25}
				position="relative"
				sx={{
					width: '100%',
				}}
				{...navigableSectionProps}
			>
				<Sentinel position="top" ref={navigationBarSentinelRef} />

				<Stack sx={galleryStackSX}>
					<Paper sx={galleryPaperSX} id="productMediaGalleryCard">
						<VideoGalleryProvider>
							<ProductGalleryTabs
								sku={selection.sku?.partNumber ?? product.partNumber}
								arQRImageBase64={arQrImageBase64}
								view360URL={view360URL}
								isArEnabled={isArEnabled}
								isVisualSupportEnabled={isVisualSupportEnabled}
							/>
						</VideoGalleryProvider>
					</Paper>
				</Stack>

				<Stack spacing={1} sx={priceAndPromoCardSX}>
					{product.type === TYPES.bundle ? null : (
						<Stack spacing={1.25}>
							<PaperCard sx={productDetailsDisplaySX.container} elevation={1}>
								<ProductDetailsDisplay />
							</PaperCard>
							<PaperCard sx={promoCardSX} id="productPriceCard">
								<EMarketingSpot
									id={PDP_PRICE_SUPERIOR_SECTION_ESPOT}
									properties={{ emsName: PDP_PRICE_SUPERIOR_SECTION_ESPOT }}
								/>
								<Box id="productPrice">
									<ProductDetailsPrice variant="xl" />
								</Box>
								<PromotionCard
									id={selection?.sku?.id as string}
									attributes={selection?.sku?.attributes}
									showDescription={true}
									partNumber={selection?.sku?.partNumber}
								/>
								<EMarketingSpot
									id={PDP_PRICE_INFERIOR_SECTION_ESPOT}
									properties={{ emsName: PDP_PRICE_INFERIOR_SECTION_ESPOT }}
								/>
							</PaperCard>
							<FreeGiftPromotionCard
								id={selection?.sku?.id as string}
								attributes={selection?.sku?.attributes}
								partNumber={selection?.sku?.partNumber}
							/>
							{!isStoreOnlyAvailable && (
								<DeliveryMethodSelector product={selection.sku ?? product} />
							)}
							<PaperCard
								sx={productDetailsDisplaySX.addToCartContainer}
								elevation={1}
								id="shopperActionsCard"
							>
								<ProductDetailsAddToCart
									isIconButton
									showQuantityInput
									showAvailabilityLabel
									showMinimumQtyLabel
								/>
							</PaperCard>

							<EMarketingSpot
								id={PDP_UX_COMPONENT_ESPOT}
								properties={{ emsName: PDP_UX_COMPONENT_ESPOT }}
							/>

							<RoomvoContainer
								sku={
									isRoomvoEnabled && isRoomvoVisualSupportEnabled ? getItemPartNumber(product) : ''
								}
								enabled={isRoomvoEnabled && isRoomvoVisualSupportEnabled}
								productType={'floor'}
							>
								<EMarketingSpot
									id={PDP_ROOMVO_TRIGGER_ESPOT}
									properties={{ emsName: PDP_ROOMVO_TRIGGER_ESPOT }}
								/>
							</RoomvoContainer>
							<ProductDetailsAttributes />
							<ProductAbout />
							<ProductAvailability />
							<EMarketingSpot
								id={PDP_CROSS_SELL_ESPOT}
								properties={{ emsName: PDP_CROSS_SELL_ESPOT }}
							/>
						</Stack>
					)}

					{/*  Uncomment to debug sticky gallery  */}
					{/* <div */}
					{/* 	style={{ */}
					{/* 		height: 1000, */}
					{/* 		backgroundColor: 'rgba(0, 0, 255, 0.1)', */}
					{/* 		width: '100%', */}
					{/* 		border: '1px dashed blue', */}
					{/* 	}} */}
					{/* /> */}
				</Stack>
			</Stack>
		</ContentProvider>
	);
};
